import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import en from "./resources/en";
import routesEn from "./routes/en";

// All 7 locales are supported, but only English ships in the initial client
// bundle — PageSpeed flagged ~99 KiB of unused JS when every locale was
// imported eagerly. Other languages load on demand as separate chunks via
// setLanguage() (picked in the LanguageSwitcher or restored from rt_lang).
export const SUPPORTED_LANGS = ["en", "de", "es", "fr", "it", "zh", "ru"] as const;
export type SupportedLang = (typeof SUPPORTED_LANGS)[number];

export const LANG_META: Record<string, { label: string; native: string; flag: string }> = {
  en: { label: "English", native: "English", flag: "🇬🇧" },
  de: { label: "German", native: "Deutsch", flag: "🇩🇪" },
  es: { label: "Spanish", native: "Español", flag: "🇪🇸" },
  fr: { label: "French", native: "Français", flag: "🇫🇷" },
  it: { label: "Italian", native: "Italiano", flag: "🇮🇹" },
  zh: { label: "Chinese", native: "中文", flag: "🇨🇳" },
  ru: { label: "Russian", native: "Русский", flag: "🇷🇺" },
};

// Static import maps so Vite emits one lazy chunk per locale.
const RESOURCE_LOADERS: Record<Exclude<SupportedLang, "en">, () => Promise<{ default: object }>> = {
  de: () => import("./resources/de"),
  es: () => import("./resources/es"),
  fr: () => import("./resources/fr"),
  it: () => import("./resources/it"),
  zh: () => import("./resources/zh"),
  ru: () => import("./resources/ru"),
};
const ROUTE_LOADERS: Record<Exclude<SupportedLang, "en">, () => Promise<{ default: object }>> = {
  de: () => import("./routes/de"),
  es: () => import("./routes/es"),
  fr: () => import("./routes/fr"),
  it: () => import("./routes/it"),
  zh: () => import("./routes/zh"),
  ru: () => import("./routes/ru"),
};

const loadedLangs = new Set<string>(["en"]);

/** Load a locale bundle on demand (no-op for en/already-loaded) and switch
 * to it. All language changes must go through here, never through
 * i18n.changeLanguage directly — otherwise the bundle may be missing. */
export async function setLanguage(lng: string): Promise<void> {
  const base = (lng || "en").split("-")[0] as SupportedLang;
  if (!(SUPPORTED_LANGS as readonly string[]).includes(base)) return;
  if (!loadedLangs.has(base) && base !== "en") {
    try {
      const [res, routes] = await Promise.all([
        RESOURCE_LOADERS[base as Exclude<SupportedLang, "en">](),
        ROUTE_LOADERS[base as Exclude<SupportedLang, "en">](),
      ]);
      i18n.addResourceBundle(
        base,
        "translation",
        { ...(res.default as object), routes: routes.default },
        true,
        true,
      );
      loadedLangs.add(base);
    } catch (e) {
      console.error(`Failed to load locale ${base}:`, e);
      return;
    }
  }
  await i18n.changeLanguage(base);
}

if (!i18n.isInitialized) {
  i18n.use(initReactI18next).init({
    resources: {
      en: { translation: { ...en, routes: routesEn } },
    },
    lng: "en",
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LANGS as unknown as string[],
    nonExplicitSupportedLngs: true,
    interpolation: { escapeValue: false },
    returnNull: false,
  });
}

export default i18n;
